var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { Button, List, Typography, Card, Modal, Form, Select, Alert, Checkbox, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import ParameterEditor from './ParameterEditor';
import { useScenarioItemValidator } from './hooks/useScenarioItemValidator';
import { Trans, useTranslation } from 'react-i18next';
import { SCENARIO_THEME } from './scenarioTheme';
const { Title } = Typography;
const getPatchedSchema = (conditionName, schema) => {
    var _a, _b;
    if (conditionName === 'filtrex' && ((_b = (_a = schema === null || schema === void 0 ? void 0 : schema.properties) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.type) === 'object') {
        const newSchema = JSON.parse(JSON.stringify(schema));
        newSchema.properties.value.type = 'string';
        return newSchema;
    }
    return schema;
};
const Step_Conditions = ({ formData, setFormData, availableConditions, availableProviders, }) => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingInfo, setEditingInfo] = useState(null);
    const [modalConditionData, setModalConditionData] = useState({});
    const [form] = Form.useForm();
    const [isMainFormValid, setIsMainFormValid] = useState(false);
    const theme = SCENARIO_THEME.conditions;
    const IconComponent = theme.icon;
    const handleAddGroup = () => {
        const newLayout = [...formData, []];
        setFormData(newLayout);
    };
    const handleRemoveGroup = (groupIndex) => {
        const newLayout = [...formData];
        newLayout.splice(groupIndex, 1);
        setFormData(newLayout);
    };
    const handleAddCondition = (groupIndex) => {
        setEditingInfo({ groupIndex });
        setModalConditionData({ params: {}, confidentialData: false });
        form.resetFields();
        setIsMainFormValid(false);
        setIsModalVisible(true);
    };
    const handleEditCondition = (groupIndex, conditionIndex) => {
        const condition = formData[groupIndex][conditionIndex];
        setEditingInfo({ groupIndex, conditionIndex });
        setModalConditionData(condition);
        form.setFieldsValue({ name: condition.name, confidentialData: condition.confidentialData || false });
        setIsMainFormValid(true);
        setIsModalVisible(true);
    };
    const handleRemoveCondition = (groupIndex, conditionIndex) => {
        const newLayout = [...formData];
        newLayout[groupIndex].splice(conditionIndex, 1);
        setFormData(newLayout);
    };
    const handleModalOk = () => {
        form.validateFields().then((values) => {
            if (!editingInfo)
                return;
            const finalCondition = {
                name: values.name,
                params: modalConditionData.params || {},
                confidentialData: values.confidentialData || false,
            };
            const newLayout = [...formData];
            if (editingInfo.conditionIndex !== undefined) {
                newLayout[editingInfo.groupIndex][editingInfo.conditionIndex] = finalCondition;
            }
            else {
                newLayout[editingInfo.groupIndex].push(finalCondition);
            }
            setFormData(newLayout);
            handleModalCancel();
        });
    };
    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingInfo(null);
    };
    const handleConditionTypeChange = (name) => {
        setModalConditionData({ name, params: {} });
    };
    const selectedConditionDef = availableConditions.find((cond) => cond.name === modalConditionData.name);
    const isEditing = (editingInfo === null || editingInfo === void 0 ? void 0 : editingInfo.conditionIndex) !== undefined;
    const patchedSchema = getPatchedSchema(selectedConditionDef === null || selectedConditionDef === void 0 ? void 0 : selectedConditionDef.name, selectedConditionDef === null || selectedConditionDef === void 0 ? void 0 : selectedConditionDef.paramsSchema);
    const isOkDisabled = useScenarioItemValidator({
        isModalVisible,
        isMainFormValid,
        modalData: modalConditionData,
        selectedDef: selectedConditionDef,
        patchedSchema,
    });
    return (React.createElement("div", null,
        React.createElement(Space, { style: { marginBottom: 24 } },
            React.createElement(IconComponent, { style: { fontSize: '20px', color: theme.color } }),
            React.createElement(Title, { level: 4, style: { margin: 0, color: theme.color } }, theme.name)),
        React.createElement(Alert, { message: React.createElement(Trans, { i18nKey: "howConditionsWorkTitle", defaults: "How Conditions Work" }), description: React.createElement("div", null,
                React.createElement("p", null,
                    React.createElement(Trans, { i18nKey: "conditionsIntro1", defaults: "Here you can set rules to decide when your scenario should run." })),
                React.createElement("p", null,
                    React.createElement(Trans, { i18nKey: "conditionsIntro2", defaults: "You can create different groups of rules. The scenario will run if <bold>all rules within any single group are true.</bold>", components: { bold: React.createElement("strong", null) } })),
                React.createElement("p", null,
                    React.createElement(Trans, { i18nKey: "conditionsIntroExample", defaults: "For example, if you have two groups:" })),
                React.createElement("ul", null,
                    React.createElement("li", null,
                        React.createElement(Trans, { i18nKey: "conditionsIntroGroup1", defaults: "<bold>Group 1</bold> contains Rule A and Rule B", components: { bold: React.createElement("strong", null) } })),
                    React.createElement("li", null,
                        React.createElement(Trans, { i18nKey: "conditionsIntroGroup2", defaults: "<bold>Group 2</bold> contains Rule C", components: { bold: React.createElement("strong", null) } }))),
                React.createElement(Trans, { i18nKey: "conditionsIntroConclusion", defaults: "The scenario will run if <bold>(Rule A and Rule B are both true)</bold> OR if <bold>(Rule C is true)</bold>.", components: { bold: React.createElement("strong", null) } })), type: "info", showIcon: true, style: { marginBottom: 20 } }),
        formData.map((group, groupIndex) => (React.createElement(Card, { key: groupIndex, title: t('conditionGroupHeader', 'Condition Group {{index}}', { index: groupIndex + 1 }), style: { marginBottom: 16 }, extra: React.createElement(Button, { danger: true, type: "link", onClick: () => handleRemoveGroup(groupIndex) },
                React.createElement(Trans, { i18nKey: "removeGroup", defaults: "Remove Group" })) },
            React.createElement(List, { dataSource: group, renderItem: (condition, conditionIndex) => {
                    const conditionDef = availableConditions.find((c) => c.name === condition.name);
                    const title = conditionDef ? conditionDef.title : condition.name;
                    return (React.createElement(React.Fragment, null,
                        React.createElement(List.Item, { actions: [
                                React.createElement(Button, { type: "link", onClick: () => handleEditCondition(groupIndex, conditionIndex) },
                                    React.createElement(Trans, { i18nKey: "edit", defaults: "Edit" })),
                                React.createElement(Button, { type: "link", danger: true, onClick: () => handleRemoveCondition(groupIndex, conditionIndex) },
                                    React.createElement(Trans, { i18nKey: "remove", defaults: "Remove" })),
                            ] },
                            React.createElement("div", null,
                                React.createElement(Typography.Text, null, title),
                                condition.confidentialData && (React.createElement(Typography.Text, { type: "secondary", style: { marginLeft: 8 } },
                                    "[",
                                    React.createElement(Trans, { i18nKey: "confidential" }),
                                    "]")))),
                        group.length > 1 && conditionIndex < group.length - 1 && (React.createElement("div", { style: { textAlign: 'center', margin: '8px 0' } },
                            React.createElement(Typography.Text, { strong: true },
                                React.createElement(Trans, { i18nKey: "andOperator", defaults: "AND" }))))));
                } }),
            React.createElement(Button, { type: "dashed", onClick: () => handleAddCondition(groupIndex), style: { marginTop: '16px' } },
                React.createElement(PlusOutlined, null),
                " ",
                React.createElement(Trans, { i18nKey: "addCondition", defaults: "Add Condition" }))))),
        React.createElement(Button, { type: "dashed", onClick: handleAddGroup, block: true },
            React.createElement(PlusOutlined, null),
            " ",
            React.createElement(Trans, { i18nKey: "addConditionGroup", defaults: "Add Condition Group" })),
        React.createElement(Modal, { title: isEditing ? t('editCondition', 'Edit Condition') : t('addConditionToGroup', 'Add Condition to Group'), open: isModalVisible, onOk: handleModalOk, onCancel: handleModalCancel, okButtonProps: { disabled: isOkDisabled }, okText: isEditing ? t('save', 'Save') : t('add', 'Add'), destroyOnClose: true, width: 700, bodyStyle: { maxHeight: '70vh', overflowY: 'auto' } },
            React.createElement(Form, { form: form, layout: "vertical", name: "condition_form_in_modal", initialValues: { name: modalConditionData.name }, onValuesChange: () => {
                    setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                        try {
                            yield form.validateFields();
                            setIsMainFormValid(true);
                        }
                        catch (_a) {
                            setIsMainFormValid(false);
                        }
                    }), 0);
                } },
                React.createElement(Form.Item, { name: "name", label: React.createElement(Trans, { i18nKey: "conditionType", defaults: "Condition Type" }), rules: [{ required: true, message: React.createElement(Trans, { i18nKey: "pleaseSelectConditionType", defaults: "Please select a condition type!" }) }] },
                    React.createElement(Select, { placeholder: t('selectConditionTypePlaceholder', 'Select a condition type'), onChange: handleConditionTypeChange, disabled: isEditing }, availableConditions.map((cond) => (React.createElement(Select.Option, { key: cond.name, value: cond.name }, cond.title))))),
                React.createElement(Form.Item, { name: "confidentialData", valuePropName: "checked", style: { marginBottom: 16 } },
                    React.createElement(Checkbox, null,
                        React.createElement(Trans, { i18nKey: "containsConfidentialData" }),
                        React.createElement(Typography.Text, { type: "secondary", style: { display: 'block', fontSize: '12px' } },
                            React.createElement(Trans, { i18nKey: "containsConfidentialDataTooltip" }))))),
            (selectedConditionDef === null || selectedConditionDef === void 0 ? void 0 : selectedConditionDef.name) === 'filtrex' && (React.createElement(Alert, { message: React.createElement(Trans, { i18nKey: "aboutFiltrexTitle", defaults: "About Filtrex Expressions" }), description: React.createElement(React.Fragment, null,
                    React.createElement("p", null,
                        React.createElement(Trans, { i18nKey: "filtrexDescription", defaults: "Filtrex allows you to write complex expressions to evaluate data from triggers and loaders." })),
                    React.createElement("p", null,
                        React.createElement("strong", null,
                            React.createElement(Trans, { i18nKey: "exampleLabel", defaults: "Example:" })),
                        ' ',
                        React.createElement("code", null,
                            "status == 'active' and (age ",
                            '>',
                            " 18 or is_vip)")),
                    React.createElement("p", null,
                        React.createElement(Trans, { i18nKey: "filtrexUsageOperators", defaults: "You can use logical operators (<c>and</c>, <c>or</c>, <c>not</c>), comparisons", components: { c: React.createElement("code", null) } }),
                        ' ',
                        "(",
                        React.createElement("code", null, "=="),
                        ", ",
                        React.createElement("code", null, "!="),
                        ", ",
                        React.createElement("code", null, '>'),
                        ",",
                        ' ',
                        React.createElement("code", null, '<'),
                        "),",
                        ' ',
                        React.createElement(Trans, { i18nKey: "filtrexUsageDotNotation", defaults: "and access nested data using dot notation (e.g., <c>user.profile.name</c>).", components: { c: React.createElement("code", null) } }))), type: "info", showIcon: true, style: { marginTop: 16 } })),
            (selectedConditionDef === null || selectedConditionDef === void 0 ? void 0 : selectedConditionDef.paramsSchema) && Object.keys(selectedConditionDef.paramsSchema).length > 0 && (React.createElement("div", null,
                React.createElement(Typography.Title, { level: 5, style: { marginTop: 16 } },
                    React.createElement(Trans, { i18nKey: "parametersTitle", defaults: "Parameters" })),
                React.createElement(ParameterEditor, { schema: patchedSchema, formData: modalConditionData.params, onChange: (params) => {
                        setModalConditionData((prev) => (Object.assign(Object.assign({}, prev), { params })));
                    }, availableProviders: availableProviders }))))));
};
export default Step_Conditions;
